import type { IsoDate } from "./types.js";
import type { CheerRow } from "../../shared/types.js";

/**
 * 「戳我的是誰」。
 *
 * 監督者自己填的暱稱同時是**顯示標籤**與**身分**——`shared/nickname.ts` 的
 * 檔頭寫得很清楚，它回答的是「戳我的是誰」而不是「這個人是誰」，冒名在朋友圈
 * 裡不是安全問題。代價是每一次「這個名字是不是我」的判斷都散在畫面裡：先前
 * `App.tsx` 有四段各自寫的內聯查詢，其中兩段問的是完全同一個問題（我今天戳過
 * 沒），卻各算一次。
 *
 * 那四段查詢底下是同一個概念，而這個 codebase 先前沒有一個地方放它。
 * `docs/adr/0005` 記著這件事的天花板：哪天真要把身分從暱稱換成裝置 ID
 * （清 cookie 就重置是已知的上限），要改的就是這裡。
 *
 * 為什麼不放 `src/data/` 的 store：這幾支沒有狀態、不需要注入 writer、
 * 不涉及回滾——它們是對一個陣列的提問。store 的深度在樂觀更新，不在查詢。
 *
 * 與 `src/domain/` 的其他模組一樣：純函式、不讀系統時間、不讀全域狀態。
 */

/**
 * 那一天有誰戳過，**照原始順序**。
 *
 * 不排序：排序是 `domain/cheers.ts` 的事，那裡有它自己的理由（監督者拿到的
 * 名單照名字排，否則第一個就是戳最多的人）。
 */
export function cheeredOn(cheers: CheerRow[], date: IsoDate): string[] {
  return cheers.filter(cheer => cheer.cheer_date === date).map(cheer => cheer.nickname);
}

/**
 * 這個人在這一天戳過了嗎。
 *
 * **沒有名字一律 false。** 先前內聯的版本會拿空字串去比對，答案剛好對——
 * 因為沒有人叫空字串。那是巧合不是規則，寫進函式裡讓它變成規則。
 */
export function cheeredBy(cheers: CheerRow[], date: IsoDate, nickname: string): boolean {
  if (!nickname) return false;
  return cheers.some(cheer => cheer.cheer_date === date && cheer.nickname === nickname);
}

/** 這個人已經填過的那一注，沒填過是 null。沒有名字一律 null，理由同上。 */
export function myPrediction<T extends { nickname: string }>(
  predictions: T[],
  nickname: string
): T | null {
  if (!nickname) return null;
  return predictions.find(prediction => prediction.nickname === nickname) || null;
}
